import { FollowType, useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { EntityFollowMenu } from "~/components/entity-follow-menu";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { tracking } from "~/data";
import { getAbsoluteUrl } from "~/lib/utils";
import type { greaterBayAreaQuery$key } from "~/queries/__generated__/greaterBayAreaQuery.graphql";

import {
  Container,
  StyledTopicFollowButton,
  StyledTopStoriesBlock,
  Subsection,
  TopicBadge,
  TopicName,
} from "./styles";

type Props = {
  className?: string;
  reference: greaterBayAreaQuery$key;
};

export const HomeGreaterBayArea: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const data = useFragment(
    graphql`
      fragment greaterBayAreaQuery on Query
      @argumentDefinitions(greaterBayAreaArticlesQueueName: { type: "String!" }) {
        topic(filter: { entityId: "324555" }) {
          ...entityLink
          ...followButtonBase
          name
        }
        greaterBayAreaArticles: queue(filter: { name: $greaterBayAreaArticlesQueueName }) {
          items(first: 4) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...topStoriesBlockQueueItemsEdge
            }
          }
        }
        greaterBayAreaTopics: queue(filter: { name: "related_topics_508929" }) {
          items(first: 200) {
            edges {
              ...entityFollowMenuQueueItemsEdge
            }
          }
        }
      }
    `,
    reference_,
  );

  const greaterBayAreaItems = data.greaterBayAreaArticles?.items?.edges ?? [];
  const topicEdges = data.greaterBayAreaTopics?.items?.edges ?? [];
  const listOfArticles = (
    data?.greaterBayAreaArticles?.items?.edges?.map(item => item.node.entityId) ?? []
  ).join(",");

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/homepage_gba",
      category: "Homepage Depth",
    }),
    [],
  );
  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "homepage_gba",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    Event,
    RecirculationWidgetImpressionEvent
  >({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    getGa4Event: impressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.HomepageGBA,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <TopicBadge>
          <TopicName reference={data.topic}>{data.topic.name}</TopicName>
          <StyledTopicFollowButton
            reference={data.topic}
            source="Homepage_gba"
            type={FollowType.Topic}
          />
        </TopicBadge>

        <Subsection>
          <EntityFollowMenu reference={topicEdges} />
        </Subsection>
        <StyledTopStoriesBlock
          reference={greaterBayAreaItems}
          onClick={(entityId, urlAlias) => {
            sendGATracking({
              action: "Click/homepage_gba",
              category: "Homepage Depth",
              label: entityId,
            });

            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                destination_url: getAbsoluteUrl(urlAlias),
                page_type: "homepage",
                widget_name: "homepage_gba",
              },
              subcategory: "widget",
            });
          }}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeGreaterBayArea.displayName = "HomeGreaterBayArea";
